import { Component, Inject, OnInit, ViewContainerRef } from "@angular/core";
import { map, Observable, shareReplay, take } from "rxjs";
import { BreakpointObserver } from "@angular/cdk/layout";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PaginatedContent } from "../../../../api/common.types";
import {
  AccountFilter,
  AccountId,
  getStatusShortName,
  MissionPartnerAccount,
  TalentAccount,
} from "../../home-admin-data/account.types";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AccountService } from "../../home-admin-data/account.service";
import { NzTableQueryParams } from "ng-zorro-antd/table";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { AccountPasswordDialogComponent } from "../../../../../../../common-ui/src/lib/account-password-dialog/account-password-dialog.component";
import { AccountStatus, AccountType } from "../../../login-page/login-page-data/login.types";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe } from "@angular/common";
import { AccountCommentsDialogComponent } from "../../../../../../../common-ui/src/lib/account-comments-dialog/account-comments-dialog.component";
import { SalaryExpectation } from "../../../../../../../common-ui/src/lib/salary-expectation-input/salary-expectation.type";
import { CmsNameValue, compareCmsNameValue } from "../../../../api/cms/cms.types";
import { CmsCachedService } from "../../../../api/cms/cms-cached.service";
import { ValidateLocation } from "../../../../../../../common-ui/src/lib/location-input/location-input.validator";
import { Clipboard } from "@angular/cdk/clipboard";
import { FilterStorageService } from "../../../../api/filter-storage/filter-storage.service";
import { FiltersPage } from "../../../../api/filter-storage/filter-storage.types";
import { ManualMatchDialogComponent } from "../../../../../../../common-ui/src/lib/manual-match-dialog/manual-match-dialog.component";
import { MatchCriteriaDialogComponent } from "../../../../../../../common-ui/src/lib/match-criteria-dialog/match-criteria-dialog.component";
import { TableStateManager } from "../../utils/table-state-manager";
import { PermissionsService } from "../../../../api/authorization/permissions.service";
import { APP_CONFIGURATION } from "../../../../../../../core/src/lib/core.di";
import { AppConfig } from "../../../../../../../core/src/lib/app-config/config.types";

const FILTERS_PAGE = FiltersPage.TALENT_LIST;
// keep in sync with the media query in admin-talent-list.component.less
const MOBILE_LAYOUT_QUERY = "(max-width: 1423px)";

@Component({
  selector: "mh-admin-talent-list",
  templateUrl: "./admin-talent-list.component.html",
  styleUrls: ["./admin-talent-list.component.less"],
})
export class AdminTalentListComponent implements OnInit {
  tableState = new TableStateManager<TalentAccount>((filter) => this.accountService.loadTalentList(filter), (res) => {
    this.getSearchField("timeRange")?.patchValue(res.timeRange);
  });

  expertFields$: Observable<Array<CmsNameValue>>;
  industry$: Observable<Array<CmsNameValue>>;
  careerLevel$: Observable<Array<CmsNameValue>>;
  degree$: Observable<Array<CmsNameValue>>;
  sector$: Observable<Array<CmsNameValue>>;
  department$: Observable<Array<CmsNameValue>>;
  isMobileLayout$: Observable<boolean>;
  canWrite = false;
  isApiLoading = false;
  searchForm!: UntypedFormGroup;
  hoveredRowIndex: number | null = null;

  get showAdditionalInfo(): boolean {
    return this.searchForm.get("additionalInfo")?.value;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private accountService: AccountService,
    private filterStorageService: FilterStorageService,
    private notificationService: NzNotificationService,
    private modal: NzModalService,
    private viewContainerRef: ViewContainerRef,
    private readonly translateService: TranslateService,
    private datePipe: DatePipe,
    private cmsCachedService: CmsCachedService,
    private clipboard: Clipboard,
    private breakpointObserver: BreakpointObserver,
    private permissionsService: PermissionsService,
    @Inject(APP_CONFIGURATION) public readonly appConfig: AppConfig,
  ) {
    this.expertFields$ = this.cmsCachedService.getUserDataByName("expert-field");
    this.industry$ = this.cmsCachedService.getUserDataByName("industry");
    this.careerLevel$ = this.cmsCachedService.getUserDataByName("career-level", false);
    this.degree$ = this.cmsCachedService.getUserDataByName("degree");
    this.sector$ = this.cmsCachedService.getUserDataByName("sector");
    this.department$ = this.cmsCachedService.getUserDataByName("expert-field");
    // render only one layout instead of hiding the other with CSS, both are heavy with 100 rows
    this.isMobileLayout$ = this.breakpointObserver.observe(MOBILE_LAYOUT_QUERY).pipe(
      map((state) => state.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    // checked once for the page instead of a mhHasPermission directive (and store subscription) per row button
    this.permissionsService
      .hasPermission("talent_list.write")
      .pipe(takeUntilDestroyed())
      .subscribe((canWrite) => (this.canWrite = canWrite));
  }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      searchTerm: [null],
      salary: { min: null, max: null },
      expertFields: [null],
      industry: [null],
      careerLevel: [null],
      location: [null, [ValidateLocation]],
      timeRange: [[]],
      additionalInfo: true,
      profileFilled: [null],
      openStatus: [null],
      degree: [null],
      sector: [null],
      wishDepartment: [null],
    });

    const storedFilters = this.filterStorageService.getStoredFilters()?.[FILTERS_PAGE];
    if (storedFilters) {
      this.searchForm.patchValue(storedFilters);
    }

    this.tableState.fetchData({ ...this.getDefaultQueryParams(), ...this.getFilterFormValues() } as AccountFilter);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.tableState.onQueryParamsChange(params, () => this.getFilterFormValues());
  }

  onPageChange(pageIndex: number): void {
    this.tableState.onPageChange(pageIndex, () => this.getFilterFormValues());
  }

  submitFilterForm(): void {
    this.filterStorageService.saveFilters(this.searchForm, FILTERS_PAGE);
    this.tableState.onQueryChange({
      paging: {
        page: 1,
        itemsOnPage: this.tableState.data?.paging?.itemsOnPage || 0,
      },
      ...this.getFilterFormValues(),
    } as AccountFilter);
  }

  getFilterFormValues() {
    const searchField = this.getSearchField("searchTerm");
    const timeRangeField = this.getSearchField("timeRange");
    const salaryField = this.getSearchField("salary");
    const expertFields = this.getSearchField("expertFields");
    const industry = this.getSearchField("industry");
    const careerLevel = this.getSearchField("careerLevel");
    const location = this.getSearchField("location");
    const profileFilled = this.getSearchField("profileFilled");
    const openStatus = this.getSearchField("openStatus");
    const degree = this.getSearchField("degree");
    const sector = this.getSearchField("sector");
    const wishDepartment = this.getSearchField("wishDepartment");

    const filterFormValues: Partial<AccountFilter> = {
      searchTerm: searchField?.value || "",
      timeRange: timeRangeField?.value || [],
      filters: [],
      groupFilters: [],
      location: location?.value,
    };

    if (salaryField?.value) {
      const salary = salaryField.value as SalaryExpectation;
      if (salary.min?.value) {
        filterFormValues.filters?.push({
          field: "SALARY_EXPECTATION_MIN",
          value: salary.min?.value,
        });
      }
      if (salary.max?.value) {
        filterFormValues.filters?.push({
          field: "SALARY_EXPECTATION_MAX",
          value: salary.max?.value,
        });
      }
    }

    if (expertFields?.value?.length) {
      filterFormValues.groupFilters?.push({
        value: expertFields.value,
        field: "EXPERT_FIELD",
      });
    }

    if (industry?.value?.length) {
      filterFormValues.groupFilters?.push({
        value: industry.value,
        field: "INDUSTRY",
      });
    }

    if (careerLevel?.value?.length) {
      filterFormValues.groupFilters?.push({
        value: careerLevel.value,
        field: "CAREER_LEVEL",
      });
    }

    if (profileFilled?.value != null) {
      filterFormValues.filters?.push({
        value: profileFilled?.value,
        field: "PROFILE_FILL",
      });
    }

    if (openStatus?.value != null) {
      filterFormValues.filters?.push({
        value: openStatus?.value,
        field: "PROFILE_STATUS",
      });
    }

    if (degree?.value?.length) {
      filterFormValues.groupFilters?.push({
        value: degree?.value,
        field: "DEGREE",
      });
    }

    if (sector?.value?.length) {
      filterFormValues.groupFilters?.push({
        value: sector?.value,
        field: "SECTOR",
      });
    }

    if (wishDepartment?.value?.length) {
      filterFormValues.groupFilters?.push({
        value: wishDepartment.value,
        field: "DEPARTMENT",
      });
    }

    return filterFormValues;
  }

  getSearchField(name: string) {
    return this.searchForm.get(name);
  }

  onSignInAs(accountId: string) {
    this.accountService.signInAs(accountId);
  }

  sendPasswordResetEmail(accountId: AccountId, email: string) {
    this.accountService.sendPasswordResetEmail(accountId, email).subscribe((success) => {
      if (success) {
        this.notificationService.success("", this.translateService.instant("admin.password-reset.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  openSetPasswordModal(account: TalentAccount | MissionPartnerAccount) {
    const modal = this.modal.create({
      nzContent: AccountPasswordDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzMaskClosable: false,
      nzData: { account },
    });
  }

  openCommentModal(account: TalentAccount | MissionPartnerAccount) {
    const modal = this.modal.create({
      nzContent: AccountCommentsDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzMaskClosable: false,
      nzCentered: true,
      nzWidth: "800px",
      nzStyle: { padding: "30px 0" },
      nzBodyStyle: { padding: "24px 24px 0" },
      nzData: { account },
      nzFooter: null,
    });
  }

  deleteAccount(accountId: AccountId, accountType = AccountType.TALENT) {
    this.accountService.deleteAccount(accountId, accountType).subscribe((success) => {
      if (success) {
        if (this.tableState.data?.items) {
          this.tableState.data.items = this.tableState.data.items.filter(
            (account: TalentAccount) => account.accountId.internalId !== accountId.internalId,
          );
        }
        this.notificationService.success("", this.translateService.instant("admin.account-delete.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  showDeleteConfirm(account: TalentAccount | MissionPartnerAccount) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.account-delete.confirm.title"),
      nzContent: `<b>${this.translateService.instant("admin.account-delete.confirm.text")}</b>`,
      nzOkText: this.translateService.instant("admin.account-delete.confirm.ok.btn"),
      nzCancelText: this.translateService.instant("admin.account-delete.confirm.cancel.btn"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.deleteAccount(account.accountId),
    });
  }

  downloadData(accountId: AccountId, accountType = AccountType.TALENT) {
    const currentDate = new Date();
    const formattedDate = this.datePipe.transform(currentDate, "yyyy-MM-dd");
    const fileName = `${formattedDate}-${accountId.internalId}_export.json`;

    this.accountService.downloadData(accountId, accountType, fileName).subscribe();
  }

  downloadTalentListCSV() {
    const currentDate = new Date();
    const formattedDate = this.datePipe.transform(currentDate, "yyyy-MM-dd");
    const fileName = `${formattedDate}_talent_list_export.csv`;

    this.isApiLoading = true;
    this.accountService.downloadTalentListCSV(fileName).subscribe(() => (this.isApiLoading = false));
  }

  exportTalentToBrevo(talent: TalentAccount) {
    this.accountService.exportTalentProfileToBrevo(talent.accountId.internalId).subscribe((result) => {
      if (result) this.notificationService.success("", this.translateService.instant("profile.exported.notification"));
    });
  }

  copyId(id: string) {
    const isCopySuccessful = this.clipboard.copy(id);
    if (isCopySuccessful) {
      this.notificationService.success("", this.translateService.instant("admin.copy-id.success"));
    }
  }

  setCommunityMemberStatus(talent: TalentAccount) {
    this.accountService
      .setCommunityMemberStatus(talent.accountId.internalId, !talent.communityMember)
      .subscribe((success) => {
        if (success) {
          talent.communityMember = !talent.communityMember;
          this.notificationService.success(
            "",
            this.translateService.instant("admin.set-community-member-status.success"),
          );
        } else {
          this.notificationService.error("", this.translateService.instant("notification.error"));
        }
      });
  }

  setConfirmedStatus(talent: TalentAccount) {
    this.accountService.addAccountConfirmedStatus(talent.accountId.internalId).subscribe((success) => {
      if (success) {
        if (!talent.statuses.includes(AccountStatus.CONFIRMED)) talent.statuses.push(AccountStatus.CONFIRMED);
        if (!talent.statuses.includes(AccountStatus.REGISTERED)) talent.statuses.push(AccountStatus.REGISTERED);
        this.notificationService.success("", this.translateService.instant("admin.add-confirmed-status.success"));
      } else {
        this.notificationService.error("", this.translateService.instant("notification.error"));
      }
    });
  }

  getSalaryExpectationLabel(salary: SalaryExpectation): string {
    return salary.min?.name + " - " + salary.max?.name;
  }

  getDefaultQueryParams() {
    const from = new Date(2023, 0, 1);
    const to = new Date(new Date().getFullYear(), 11, 31);

    return {
      paging: {
        page: 1,
        itemsOnPage: 50,
      },
      searchTerm: "",
      timeRange: [from, to],
    };
  }

  openManualMatchModal(talent: TalentAccount): void {
    const modal = this.modal.create({
      nzTitle: this.translateService.instant("admin.manual-match.modal.title"),
      nzContent: ManualMatchDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { talent },
      nzFooter: null,
    });

    modal.afterClose.subscribe((result) => {
      if (result) {
        this.accountService.addTalentToJobOffer(talent.accountId.internalId, result.jobOfferId).subscribe((success) => {
          if (success) {
            this.notificationService.success("", this.translateService.instant("admin.manual-match.success"));
          } else {
            this.notificationService.error("", this.translateService.instant("notification.error"));
          }
        });
      }
    });
  }

  openMatchCriteriaModal(talent: TalentAccount): void {
    const modal = this.modal.create({
      nzTitle: this.translateService.instant("admin.match-criteria.modal.title"),
      nzContent: MatchCriteriaDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { talent },
      nzFooter: null,
    });
  }

  protected readonly getStatusShortName = getStatusShortName;
  protected readonly compareCmsNameValue = compareCmsNameValue;
  protected readonly AccountStatus = AccountStatus;
}
